// Resume42.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IconButton, Typography, Box, Button, Paper, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { ChevronLeft, ChevronRight, Reorder, DragIndicator } from '@mui/icons-material';

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
};

const pageSizeMap = {
  A4: { width: '210mm', minHeight: '297mm', height: '297mm' },
  Letter: { width: '216mm', minHeight: '279mm', height: '279mm' },
  Legal: { width: '216mm', minHeight: '356mm', height: '356mm' },
  A3: { width: '297mm', minHeight: '420mm', height: '420mm' },
  Executive: { width: '184mm', minHeight: '267mm', height: '267mm' },
};

const pageSizePxMap = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const DEFAULT_FORM_DATA = {};
const DEFAULT_WORK_EXPERIENCES = [];

const Resume42 = ({
  color: initialColor = '#5d4037',
  font: initialFont = 'Arial, Helvetica, sans-serif',
  fontSize: initialFontSize = '14px',
  headingSize: initialHeadingSize = '24px',
  fontStyle: initialFontStyle = 'normal',
  sectionSpacing: initialSectionSpacing = 50,
  paragraphSpacing: initialParagraphSpacing = 30,
  lineSpacing: initialLineSpacing = 20,
  topBottomMargin: initialTopBottomMargin = 40,
  sideMargins: initialSideMargins = 40,
  paragraphIndent: initialParagraphIndent = 0,
  lineWeight: initialLineWeight = 1,
  pageSize: initialPageSize = 'A4',
  photo: photoProp = '',
  formData: formDataProp = DEFAULT_FORM_DATA,
  workExperiences: workExperiencesProp = DEFAULT_WORK_EXPERIENCES,
  exporting = false,
  designSettings = {},
}) => {
  const color = designSettings.color || initialColor;
  const font = designSettings.font || initialFont;
  const fontSize = designSettings.fontSize || initialFontSize;
  const headingSize = designSettings.headingSize || initialHeadingSize;
  const fontStyle = designSettings.fontStyle || initialFontStyle;
  const sectionSpacing = designSettings.sectionSpacing !== undefined ? designSettings.sectionSpacing : initialSectionSpacing;
  const paragraphSpacing = designSettings.paragraphSpacing !== undefined ? designSettings.paragraphSpacing : initialParagraphSpacing;
  const lineSpacing = designSettings.lineSpacing !== undefined ? designSettings.lineSpacing : initialLineSpacing;
  const topBottomMargin = designSettings.topBottomMargin !== undefined ? designSettings.topBottomMargin : initialTopBottomMargin;
  const sideMargins = designSettings.sideMargins !== undefined ? designSettings.sideMargins : initialSideMargins;
  const paragraphIndent = designSettings.paragraphIndent !== undefined ? designSettings.paragraphIndent : initialParagraphIndent;
  const lineWeight = designSettings.lineWeight !== undefined ? designSettings.lineWeight : initialLineWeight;
  const pageSize = designSettings.pageSize || initialPageSize;

  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const pagePx = pageSizePxMap[pageSize] || pageSizePxMap.A4;

  const safeJSON = (str, fallback = null) => {
    try { return JSON.parse(str) || fallback; } catch { return fallback; }
  };

  const ensureArray = (val) => (Array.isArray(val) ? val : []);

  // ── Pagination states (like Resume39) ──────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);   // [{ rightIds: string[] }]
  const mainContentRef = useRef(null);      // hidden measure container

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', city: '', country: '', pinCode: '',
    phone: '', email: '', currentPosition: '', linkedIn: '', website: '', drivingLicence: '',
  });
  const [photo, setPhoto] = useState('');
  const [workHistory, setWorkHistory] = useState([]);
  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState('');
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [customSections, setCustomSections] = useState({});

  const formatDate = (month, year) => [month, year].filter(Boolean).join(' ');

  // ── Default section order ────────────────────────────────────────────────
  const defaultLeftSectionOrder = ['contact', 'skills', 'languages', 'interests', 'software'];
  const defaultRightSectionOrder = [
    'profile', 'workExperience', 'education', 'accomplishments',
    'certifications', 'volunteering', 'websites', 'additionalInfo', 'references'
  ];

  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ── Load section order and data on mount ─────────────────────────────────
  useEffect(() => {
    try {
      const saved = localStorage.getItem('resume42LeftSectionOrder');
      if (saved) setLeftSectionOrder(ensureArray(safeJSON(saved, [])).filter(Boolean));
      else setLeftSectionOrder(defaultLeftSectionOrder);

      const savedRight = localStorage.getItem('resume42RightSectionOrder');
      if (savedRight) setRightSectionOrder(ensureArray(safeJSON(savedRight, [])).filter(Boolean));
      else setRightSectionOrder(defaultRightSectionOrder);
    } catch (e) {
      setLeftSectionOrder(defaultLeftSectionOrder);
      setRightSectionOrder(defaultRightSectionOrder);
    }
  }, []);

  // ── Persist section orders ──────────────────────────────────────────────
  useEffect(() => {
    if (leftSectionOrder.length > 0) {
      localStorage.setItem('resume42LeftSectionOrder', JSON.stringify(leftSectionOrder));
    }
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) {
      localStorage.setItem('resume42RightSectionOrder', JSON.stringify(rightSectionOrder));
    }
  }, [rightSectionOrder]);

  // ── Load form data ────────────────────────────────────────────────────────
  useEffect(() => {
    const storedPersonal = localStorage.getItem('personalData');
    if (storedPersonal) {
      const data = safeJSON(storedPersonal, {});
      setFormData(prev => ({ ...prev, ...data }));
    }
    if (formDataProp && Object.keys(formDataProp).length > 0) {
      setFormData(prev => ({ ...prev, ...formDataProp }));
    }

    if (photoProp) setPhoto(photoProp);
    else {
      const storedPhoto = localStorage.getItem('profilePhoto') || localStorage.getItem('photo');
      if (storedPhoto) setPhoto(storedPhoto);
    }

    if (Array.isArray(workExperiencesProp) && workExperiencesProp.length > 0) {
      setWorkHistory(workExperiencesProp);
    } else {
      const work = localStorage.getItem('workExperiences');
      if (work) setWorkHistory(ensureArray(safeJSON(work, [])));
    }

    const edu = localStorage.getItem('educationEntries');
    if (edu) setEducationEntries(ensureArray(safeJSON(edu, [])));

    const sk = localStorage.getItem('skills');
    if (sk) setSavedSkills(ensureArray(safeJSON(sk, [])));

    const summaries = ensureArray(safeJSON(localStorage.getItem('summaries'), []));
    if (summaries.length > 0) setSavedSummary(summaries[summaries.length - 1]);

    const add = localStorage.getItem('additionalInfo');
    if (add) setSavedAdditionalInfo(ensureArray(safeJSON(add, [])));

    const lang = localStorage.getItem('languagesList');
    if (lang) setSavedLanguages(ensureArray(safeJSON(lang, [])));

    const acc = localStorage.getItem('accomplishmentsList');
    if (acc) setSavedAccomplishments(ensureArray(safeJSON(acc, [])));

    const cert = localStorage.getItem('certificationsList');
    if (cert) setSavedCertifications(ensureArray(safeJSON(cert, [])));

    const soft = localStorage.getItem('softwareSkills');
    if (soft) setSoftwareSkills(ensureArray(safeJSON(soft, [])));

    const ref = localStorage.getItem('referencesList');
    if (ref) setSavedReferences(ensureArray(safeJSON(ref, [])));

    const vol = localStorage.getItem('volunteering');
    if (vol) setSavedVolunteering(ensureArray(safeJSON(vol, [])));

    const int = localStorage.getItem('interests');
    if (int) setSavedInterests(ensureArray(safeJSON(int, [])));

    const web = localStorage.getItem('websites');
    if (web) setSavedWebsites(ensureArray(safeJSON(web, [])));

    const userId = localStorage.getItem('userId');
    let sectionsData = {};
    if (userId) {
      const list = ensureArray(safeJSON(localStorage.getItem(`current_custom_sections_${userId}`), []));
      if (Array.isArray(list)) {
        list.forEach(name => {
          const parsed = safeJSON(localStorage.getItem(`custom_${userId}_${name}`), {});
          const items = ensureArray(parsed.items || []).filter(v => v && v.trim());
          if (items.length) sectionsData[name] = items;
        });
      }
    }
    setCustomSections(sectionsData);
  }, [photoProp, formDataProp, workExperiencesProp]);

  // Sync custom sections into right order
  useEffect(() => {
    const customIds = Object.keys(customSections).map((n) => `custom_${n}`);
    setRightSectionOrder((prev) => {
      const base = prev.filter((s) => !s.startsWith("custom_"));
      return [...base, ...customIds];
    });
  }, [customSections]);

  const fullName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim() || 'YOUR NAME';
  const position = formData.currentPosition || '';

  const formatAddress = () => {
    return [formData.city, formData.country, formData.pinCode].filter(Boolean).join(', ');
  };

  const getSkillName = (skill) => {
    if (!skill) return '';
    if (typeof skill === 'string') return skill;
    return skill.name || skill.skillName || skill.skill || skill.category || '';
  };

  const getLangText = (lang) => {
    if (!lang) return '';
    if (typeof lang === 'string') return lang;
    const name = lang.language || lang.name || '';
    const level = lang.level || lang.proficiency || '';
    return level ? `${name} - ${level}` : name;
  };

  // ── Styling Helpers ───────────────────────────────────────────────────────
  const leftHeadingStyle = {
    fontSize: headingSize,
    fontWeight: 'bold',
    color: color,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: `${paragraphSpacing / 2}px`,
    marginTop: `${sectionSpacing / 3}px`,
  };

  const rightHeadingStyle = {
    fontSize: headingSize,
    fontWeight: 'bold',
    color: color,
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    borderBottom: `${lineWeight}px solid ${color}44`,
    paddingBottom: '6px',
    marginBottom: `${paragraphSpacing / 2}px`,
    marginTop: `${sectionSpacing / 3}px`,
  };

  const ulStyle = {
    paddingLeft: `${20 + paragraphIndent}px`,
    margin: '0',
    fontSize: fontSize,
    lineHeight: `${lineSpacing / 20}`,
  };

  const liStyle = { marginBottom: `${paragraphSpacing / 10}px` };

  const getImageSource = (imgSrc) => {
    if (!imgSrc) return null;
    if (imgSrc.startsWith('data:') || imgSrc.startsWith('http://') || imgSrc.startsWith('https://')) {
      return imgSrc;
    }
    if (imgSrc.startsWith('/')) {
      return imgSrc;
    }
    return `/api/uploads/${imgSrc}` || imgSrc;
  };

  // ── Drag & Drop Handlers (like Resume39) ──────────────────────────────────
  const mkDragStart = (side, order) => (e, idx) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ idx, side, id: order[idx] }));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragOver = (e, idx, side) => {
    e.preventDefault();
    setDragOverIndex(idx);
    setDragOverSide(side);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleDragEnd = e => {
    e.currentTarget.style.opacity = "1";
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleDrop = (e, targetIdx, targetSide) => {
    e.preventDefault();
    const { idx: srcIdx, side: srcSide, id } = JSON.parse(e.dataTransfer.getData("text/plain"));
    let lo = [...leftSectionOrder], ro = [...rightSectionOrder];
    if (srcSide === targetSide) {
      const arr = srcSide === "left" ? lo : ro;
      const [m] = arr.splice(srcIdx, 1);
      arr.splice(targetIdx, 0, m);
    } else {
      (srcSide === "left" ? lo : ro).splice(srcIdx, 1);
      (targetSide === "left" ? lo : ro).splice(targetIdx, 0, id);
    }
    setLeftSectionOrder(lo);
    localStorage.setItem("resume42LeftSectionOrder", JSON.stringify(lo));
    setRightSectionOrder(ro);
    localStorage.setItem("resume42RightSectionOrder", JSON.stringify(ro));
    setDragOverIndex(null);
    setDragOverSide(null);
    if (e.currentTarget) e.currentTarget.style.opacity = "1";
  };

  const resetAll = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
    localStorage.setItem("resume42LeftSectionOrder", JSON.stringify(defaultLeftSectionOrder));
    localStorage.setItem("resume42RightSectionOrder", JSON.stringify(defaultRightSectionOrder));
  };

  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  const getSectionTitle = (sectionId) => {
    const titles = {
      contact: "Contact",
      education: "Education",
      skills: "Skills",
      software: "Software",
      languages: "Languages",
      interests: "Interests",
      profile: "Profile",
      workExperience: "Work Experience",
      accomplishments: "Accomplishments",
      certifications: "Certifications",
      volunteering: "Volunteering",
      websites: "Websites",
      additionalInfo: "Additional Info",
      references: "References"
    };
    if (sectionId.startsWith("custom_")) return sectionId.replace("custom_", "").toUpperCase();
    return titles[sectionId] || sectionId;
  };

  // ─── Render Left Sections ─────────────────────────────────────────────────
  const renderLeftSection = (sectionId) => {
    switch (sectionId) {
      case 'contact':
        return (
          <div key="contact">
            <h3 style={leftHeadingStyle}>CONTACT ME</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: `${paragraphSpacing / 3}px`, fontSize: fontSize }}>
              {formData.phone && <div style={{ paddingLeft: `${paragraphIndent}px`, display: 'flex', alignItems: 'center', gap: '8px' }}><span>📞</span> {formData.phone}</div>}
              {formData.email && <div style={{ paddingLeft: `${paragraphIndent}px`, display: 'flex', alignItems: 'center', gap: '8px', wordBreak: 'break-all' }}><span>✉️</span> {formData.email}</div>}
              {formatAddress() && <div style={{ paddingLeft: `${paragraphIndent}px`, display: 'flex', alignItems: 'center', gap: '8px' }}><span>📍</span> {formatAddress()}</div>}
              {formData.website && <div style={{ paddingLeft: `${paragraphIndent}px`, display: 'flex', alignItems: 'center', gap: '8px' }}><span>🌐</span> {formData.website.replace(/^https?:\/\/(www\.)?/, '')}</div>}
              {formData.linkedIn && <div style={{ paddingLeft: `${paragraphIndent}px`, display: 'flex', alignItems: 'center', gap: '8px' }}><span>🔗</span> {formData.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}</div>}
              {formData.drivingLicence && <div style={{ paddingLeft: `${paragraphIndent}px`, display: 'flex', alignItems: 'center', gap: '8px' }}><span>🚗</span> DL: {formData.drivingLicence}</div>}
            </div>
          </div>
        );

      case 'profile':
        return savedSummary && typeof savedSummary === 'string' ? (
          <div key="profile">
            <h3 style={leftHeadingStyle}>PROFILE</h3>
            <p style={{ fontSize: fontSize, lineHeight: `${lineSpacing / 20}`, color: '#444', paddingLeft: `${paragraphIndent}px`, margin: 0 }}>
              {savedSummary}
            </p>
          </div>
        ) : null;

      case 'skills': {
        const activeSkills = ensureArray(savedSkills).filter(Boolean);
        return activeSkills.length > 0 ? (
          <div key="skills">
            <h3 style={leftHeadingStyle}>SKILLS</h3>
            <ul style={ulStyle}>
              {activeSkills.map((skill, i) => <li key={i} style={liStyle}>{getSkillName(skill)}</li>)}
            </ul>
          </div>
        ) : null;
      }

      case 'software': {
        const activeSoft = ensureArray(softwareSkills).filter(Boolean);
        return activeSoft.length > 0 ? (
          <div key="software">
            <h3 style={leftHeadingStyle}>SOFTWARE</h3>
            <ul style={ulStyle}>
              {activeSoft.map((s, i) => {
                const name = typeof s === 'string' ? s : (s?.name || s?.software || '');
                const level = (s && typeof s === 'object') ? (s.level || s.rating || '') : '';
                return <li key={i} style={liStyle}>{name}{level ? ` - ${level}%` : ''}</li>;
              })}
            </ul>
          </div>
        ) : null;
      }

      case 'languages': {
        const activeLangs = ensureArray(savedLanguages).filter(Boolean);
        return activeLangs.length > 0 ? (
          <div key="languages">
            <h3 style={leftHeadingStyle}>LANGUAGES</h3>
            <ul style={ulStyle}>
              {activeLangs.map((lang, i) => <li key={i} style={liStyle}>{getLangText(lang)}</li>)}
            </ul>
          </div>
        ) : null;
      }

      case 'interests': {
        const activeInterests = ensureArray(savedInterests).filter(Boolean);
        return activeInterests.length > 0 ? (
          <div key="interests">
            <h3 style={leftHeadingStyle}>INTERESTS</h3>
            <ul style={ulStyle}>
              {activeInterests.map((item, i) => {
                const text = typeof item === 'string' ? item : (item?.interest || item?.name || '');
                return <li key={i} style={liStyle}>{text}</li>;
              })}
            </ul>
          </div>
        ) : null;
      }

      case 'workExperience': {
        const activeWork = ensureArray(workHistory).filter(Boolean);
        return activeWork.length > 0 ? (
          <div key="workExperience">
            <h3 style={leftHeadingStyle}>WORK EXPERIENCE</h3>
            {activeWork.map((job, i) => {
              if (!job || typeof job !== 'object') return null;
              const start = formatDate(job.startMonth, job.startYear) || job.startDate || '';
              const end = (job.current || job.currentlyWorking) ? 'Present' : (formatDate(job.endMonth, job.endYear) || job.endDate || '');
              return (
                <div key={i} style={{ marginBottom: `${paragraphSpacing / 2}px`, fontSize: fontSize }}>
                  <strong>{job.employer || job.company || job.companyName || ''}</strong>
                  <div style={{ color: '#555' }}>{start}{start && end ? ' - ' : ''}{end}</div>
                  <div style={{ color: '#555' }}>{job.jobTitle || job.position || job.role || ''}</div>
                </div>
              );
            })}
          </div>
        ) : null;
      }

      case 'education': {
        const activeEdu = ensureArray(educationEntries).filter(Boolean);
        return activeEdu.length > 0 ? (
          <div key="education">
            <h3 style={leftHeadingStyle}>EDUCATION</h3>
            {activeEdu.map((edu, i) => {
              if (!edu || typeof edu !== 'object') return null;
              const isSchool = edu.educationLevel === 'school';
              const school = isSchool ? edu.schoolName : (edu.collegeName || edu.schoolName || 'Institution');
              const degree = isSchool ? `${edu.standard || ''} Standard` : (edu.degreeName || edu.degree || '');
              return (
                <div key={i} style={{ marginBottom: `${paragraphSpacing / 2}px`, fontSize: fontSize }}>
                  <strong>{school}</strong>
                  <div style={{ color: '#555' }}>{edu.gradYear || ''} {degree}</div>
                </div>
              );
            })}
          </div>
        ) : null;
      }

      case 'accomplishments': {
        const activeAccs = ensureArray(savedAccomplishments).filter(Boolean);
        return activeAccs.length > 0 ? (
          <div key="accomplishments">
            <h3 style={leftHeadingStyle}>ACCOMPLISHMENTS</h3>
            <ul style={ulStyle}>
              {activeAccs.map((acc, i) => {
                const text = typeof acc === 'string' ? acc : (acc?.title || acc?.description || acc?.name || '');
                return <li key={i} style={liStyle}>{text}</li>;
              })}
            </ul>
          </div>
        ) : null;
      }

      case 'certifications': {
        const activeCerts = ensureArray(savedCertifications).filter(Boolean);
        return activeCerts.length > 0 ? (
          <div key="certifications">
            <h3 style={leftHeadingStyle}>CERTIFICATIONS</h3>
            <ul style={ulStyle}>
              {activeCerts.map((c, i) => {
                if (!c) return null;
                const name = c.name || c.certName || c.title || '';
                return <li key={i} style={liStyle}>{name}</li>;
              })}
            </ul>
          </div>
        ) : null;
      }

      case 'volunteering': {
        const activeVols = ensureArray(savedVolunteering).filter(Boolean);
        return activeVols.length > 0 ? (
          <div key="volunteering">
            <h3 style={leftHeadingStyle}>VOLUNTEERING</h3>
            {activeVols.map((vol, i) => {
              if (!vol) return null;
              const role = typeof vol === 'string' ? vol : (vol.subtopic || vol.role || vol.title || '');
              const org = typeof vol === 'object' ? (vol.organization || vol.org || '') : '';
              return (
                <div key={i} style={{ marginBottom: `${paragraphSpacing / 2}px`, fontSize: fontSize }}>
                  <strong>{role}{org ? ` — ${org}` : ''}</strong>
                </div>
              );
            })}
          </div>
        ) : null;
      }

      case 'websites': {
        const activeWebs = ensureArray(savedWebsites).filter(Boolean).length > 0
          ? ensureArray(savedWebsites).filter(Boolean)
          : (formData.website ? [formData.website] : []);
        return activeWebs.length > 0 ? (
          <div key="websites">
            <h3 style={leftHeadingStyle}>WEBSITES</h3>
            <ul style={ulStyle}>
              {activeWebs.map((site, i) => {
                if (!site) return null;
                const url = typeof site === 'string' ? site : (site.url || site.link || '');
                const name = typeof site === 'object' ? (site.label ? `${site.label.replace(/_/g, ' ')}: ${url}` : url) : url;
                return (
                  <li key={i} style={liStyle}>
                    <a href={url.startsWith('http') ? url : `https://${url}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
                      {name || url}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;
      }

      case 'references': {
        const activeRefs = ensureArray(savedReferences).filter(Boolean);
        return activeRefs.length > 0 ? (
          <div key="references">
            <h3 style={leftHeadingStyle}>REFERENCES</h3>
            {activeRefs.map((ref, i) => {
              if (!ref) return null;
              if (typeof ref === 'string') return <div key={i} style={{ fontSize: fontSize, marginBottom: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{ref}</div>;
              const name = ref.refName || ref.name || '';
              return <div key={i} style={{ fontSize: fontSize, marginBottom: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}><strong>{name}</strong></div>;
            })}
          </div>
        ) : null;
      }

      case 'additionalInfo': {
        const activeInfo = ensureArray(savedAdditionalInfo).filter(Boolean);
        return activeInfo.length > 0 ? (
          <div key="additionalInfo">
            <h3 style={leftHeadingStyle}>ADDITIONAL INFO</h3>
            <ul style={ulStyle}>
              {activeInfo.map((info, i) => {
                const text = typeof info === 'string' ? info : (info?.info || info?.text || info?.description || '');
                return <li key={i} style={liStyle}>{text}</li>;
              })}
            </ul>
          </div>
        ) : null;
      }

      default:
        if (sectionId.startsWith("custom_")) {
          const name = sectionId.replace("custom_", "");
          const items = ensureArray(customSections[name]).filter(Boolean);
          return items.length > 0 ? (
            <div key={sectionId}>
              <h3 style={leftHeadingStyle}>{name.toUpperCase()}</h3>
              <ul style={ulStyle}>
                {items.map((item, i) => <li key={i} style={liStyle}>{item}</li>)}
              </ul>
            </div>
          ) : null;
        }
        return null;
    }
  };

  // ─── Render Right Sections ────────────────────────────────────────────────
  const renderRightSection = (sectionId, filterIds = null) => {
    if (filterIds !== null && !filterIds.includes(sectionId)) return null;

    switch (sectionId) {
      case 'profile':
        return savedSummary && typeof savedSummary === 'string' ? (
          <div key="profile" className="resume42-section" data-section-id="profile">
            <h2 style={rightHeadingStyle}>PROFILE</h2>
            <p style={{ fontSize: fontSize, lineHeight: `${lineSpacing / 20}`, color: '#444', paddingLeft: `${paragraphIndent}px`, margin: 0 }}>
              {savedSummary}
            </p>
          </div>
        ) : null;

      case 'contact':
        return (
          <div key="contact" className="resume42-section" data-section-id="contact">
            <h2 style={rightHeadingStyle}>CONTACT ME</h2>
            <div style={{ display: 'flex', gap: `${paragraphSpacing}px`, flexWrap: 'wrap', fontSize: fontSize }}>
              {formData.phone && <div>📞 {formData.phone}</div>}
              {formData.email && <div style={{ wordBreak: 'break-all' }}>✉️ {formData.email}</div>}
              {formatAddress() && <div>📍 {formatAddress()}</div>}
              {formData.website && <div>🌐 {formData.website.replace(/^https?:\/\/(www\.)?/, '')}</div>}
              {formData.linkedIn && <div>🔗 {formData.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}</div>}
              {formData.drivingLicence && <div>🚗 DL: {formData.drivingLicence}</div>}
            </div>
          </div>
        );

      case 'skills': {
        const activeSkills = ensureArray(savedSkills).filter(Boolean);
        return activeSkills.length > 0 ? (
          <div key="skills" className="resume42-section" data-section-id="skills">
            <h2 style={rightHeadingStyle}>SKILLS</h2>
            <ul style={ulStyle}>
              {activeSkills.map((skill, i) => <li key={i} style={liStyle}>{getSkillName(skill)}</li>)}
            </ul>
          </div>
        ) : null;
      }

      case 'software': {
        const activeSoft = ensureArray(softwareSkills).filter(Boolean);
        return activeSoft.length > 0 ? (
          <div key="software" className="resume42-section" data-section-id="software">
            <h2 style={rightHeadingStyle}>SOFTWARE</h2>
            <ul style={ulStyle}>
              {activeSoft.map((s, i) => {
                const name = typeof s === 'string' ? s : (s?.name || s?.software || '');
                const level = (s && typeof s === 'object') ? (s.level || s.rating || '') : '';
                return <li key={i} style={liStyle}>{name}{level ? ` - ${level}%` : ''}</li>;
              })}
            </ul>
          </div>
        ) : null;
      }

      case 'languages': {
        const activeLangs = ensureArray(savedLanguages).filter(Boolean);
        return activeLangs.length > 0 ? (
          <div key="languages" className="resume42-section" data-section-id="languages">
            <h2 style={rightHeadingStyle}>LANGUAGES</h2>
            <ul style={ulStyle}>
              {activeLangs.map((lang, i) => <li key={i} style={liStyle}>{getLangText(lang)}</li>)}
            </ul>
          </div>
        ) : null;
      }

      case 'interests': {
        const activeInterests = ensureArray(savedInterests).filter(Boolean);
        return activeInterests.length > 0 ? (
          <div key="interests" className="resume42-section" data-section-id="interests">
            <h2 style={rightHeadingStyle}>INTERESTS</h2>
            <ul style={ulStyle}>
              {activeInterests.map((item, i) => {
                const text = typeof item === 'string' ? item : (item?.interest || item?.name || '');
                return <li key={i} style={liStyle}>{text}</li>;
              })}
            </ul>
          </div>
        ) : null;
      }

      case 'workExperience': {
        const activeWork = ensureArray(workHistory).filter(Boolean);
        return activeWork.length > 0 ? (
          <div key="workExperience" className="resume42-section" data-section-id="workExperience">
            <h2 style={rightHeadingStyle}>WORK EXPERIENCE</h2>
            {activeWork.map((job, i) => {
              if (!job || typeof job !== 'object') return null;
              const start = formatDate(job.startMonth, job.startYear) || job.startDate || '';
              const end = (job.current || job.currentlyWorking) ? 'Present' : (formatDate(job.endMonth, job.endYear) || job.endDate || '');
              return (
                <div key={i} style={{ marginBottom: `${paragraphSpacing}px` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                    <h3 style={{ fontSize: fontSize, margin: '0', fontWeight: 'bold' }}>{job.employer || job.company || job.companyName || ''}</h3>
                    <span style={{ fontSize: fontSize, color: '#555' }}>{start}{start && end ? ' - ' : ''}{end}</span>
                  </div>
                  <p style={{ margin: '3px 0', color: '#555', fontSize: fontSize }}>{job.jobTitle || job.position || job.role || ''}</p>
                  {job.description && (
                    <div style={{ marginTop: '6px', fontSize: fontSize, lineHeight: `${lineSpacing / 20}`, paddingLeft: `${paragraphIndent}px` }}
                      dangerouslySetInnerHTML={{
                        __html: job.description
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\*(.*?)\*/g, '<em>$1</em>')
                          .replace(/\n/g, '<br/>'),
                      }} />
                  )}
                </div>
              );
            })}
          </div>
        ) : null;
      }

      case 'education': {
        const activeEdu = ensureArray(educationEntries).filter(Boolean);
        return activeEdu.length > 0 ? (
          <div key="education" className="resume42-section" data-section-id="education">
            <h2 style={rightHeadingStyle}>EDUCATION</h2>
            {activeEdu.map((edu, i) => {
              if (!edu || typeof edu !== 'object') return null;
              const isSchool = edu.educationLevel === 'school';
              const school = isSchool ? edu.schoolName : (edu.collegeName || edu.schoolName || 'Institution');
              const degree = isSchool ? `${edu.standard || ''} Standard` : (edu.degreeName || edu.degree || '');
              const field = isSchool ? (edu.stream || '') : (edu.specialization || edu.degreeType || '');
              const loc = isSchool ? edu.schoolLocation : (edu.collegeLocation || edu.schoolLocation || '');
              const grad = formatDate(edu.gradMonth, edu.gradYear);
              const extra = [edu.cgpa && `CGPA: ${edu.cgpa}`, edu.percentage && `${edu.percentage}%`, edu.grade].filter(Boolean).join(' | ');
              return (
                <div key={i} style={{ marginBottom: `${paragraphSpacing}px` }}>
                  <h3 style={{ fontSize: fontSize, margin: '0', fontWeight: 'bold' }}>{school}</h3>
                  <p style={{ margin: '3px 0', color: '#555', fontSize: fontSize }}>
                    {grad}{grad && degree ? ' • ' : ''}{degree}{field ? ` - ${field}` : ''}
                  </p>
                  {loc && <p style={{ margin: '3px 0', color: '#555', fontSize: fontSize, fontStyle: 'italic' }}>{loc}</p>}
                  {extra && <p style={{ margin: '3px 0', fontSize: fontSize }}>{extra}</p>}
                </div>
              );
            })}
          </div>
        ) : null;
      }

      case 'accomplishments': {
        const activeAccs = ensureArray(savedAccomplishments).filter(Boolean);
        return activeAccs.length > 0 ? (
          <div key="accomplishments" className="resume42-section" data-section-id="accomplishments">
            <h2 style={rightHeadingStyle}>ACCOMPLISHMENTS</h2>
            <ul style={ulStyle}>
              {activeAccs.map((acc, i) => {
                const text = typeof acc === 'string' ? acc : (acc?.title || acc?.description || acc?.name || '');
                return <li key={i} style={liStyle}>{text}</li>;
              })}
            </ul>
          </div>
        ) : null;
      }

      case 'certifications': {
        const activeCerts = ensureArray(savedCertifications).filter(Boolean);
        return activeCerts.length > 0 ? (
          <div key="certifications" className="resume42-section" data-section-id="certifications">
            <h2 style={rightHeadingStyle}>CERTIFICATIONS</h2>
            <ul style={ulStyle}>
              {activeCerts.map((c, i) => {
                if (!c) return null;
                const name = c.name || c.certName || c.title || '';
                const provider = c.provider || c.organization || '';
                const year = c.year || c.date || '';
                return <li key={i} style={liStyle}>{name}{provider ? ` - ${provider}` : ''}{year ? ` (${year})` : ''}</li>;
              })}
            </ul>
          </div>
        ) : null;
      }

      case 'volunteering': {
        const activeVols = ensureArray(savedVolunteering).filter(Boolean);
        return activeVols.length > 0 ? (
          <div key="volunteering" className="resume42-section" data-section-id="volunteering">
            <h2 style={rightHeadingStyle}>VOLUNTEERING</h2>
            {activeVols.map((vol, i) => {
              if (!vol) return null;
              const role = typeof vol === 'string' ? vol : (vol.subtopic || vol.role || vol.title || '');
              const org = typeof vol === 'object' ? (vol.organization || vol.org || '') : '';
              const from = typeof vol === 'object' ? (vol.fromDate || vol.startDate || '') : '';
              const to = typeof vol === 'object' ? (vol.toDate || vol.endDate || '') : '';
              const desc = typeof vol === 'object' ? (vol.content || vol.description || '') : '';
              return (
                <div key={i} style={{ marginBottom: `${paragraphSpacing}px` }}>
                  <h3 style={{ fontSize: fontSize, margin: '0', fontWeight: 'bold' }}>{role}{org ? ` — ${org}` : ''}</h3>
                  {(from || to) && <p style={{ margin: '3px 0', color: '#555', fontSize: fontSize }}>{from}{from && to ? ' - ' : ''}{to}</p>}
                  {desc && <p style={{ margin: '6px 0 0 0', fontSize: fontSize, paddingLeft: `${paragraphIndent}px` }}>{desc}</p>}
                </div>
              );
            })}
          </div>
        ) : null;
      }

      case 'websites': {
        const activeWebs = ensureArray(savedWebsites).filter(Boolean).length > 0
          ? ensureArray(savedWebsites).filter(Boolean)
          : (formData.website ? [formData.website] : []);
        return activeWebs.length > 0 ? (
          <div key="websites" className="resume42-section" data-section-id="websites">
            <h2 style={rightHeadingStyle}>WEBSITES & PROFILES</h2>
            <ul style={ulStyle}>
              {activeWebs.map((site, i) => {
                if (!site) return null;
                const url = typeof site === 'string' ? site : (site.url || site.link || '');
                const name = typeof site === 'object' ? (site.label ? `${site.label.replace(/_/g, ' ')}: ${url}` : url) : url;
                return (
                  <li key={i} style={liStyle}>
                    <a href={url.startsWith('http') ? url : `https://${url}`} target="_blank" rel="noopener noreferrer"
                      style={{ color: '#1976d2', textDecoration: 'none', fontSize: fontSize }}>
                      {name || url}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;
      }

      case 'references': {
        const activeRefs = ensureArray(savedReferences).filter(Boolean);
        return activeRefs.length > 0 ? (
          <div key="references" className="resume42-section" data-section-id="references">
            <h2 style={rightHeadingStyle}>REFERENCES</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
              {activeRefs.map((ref, i) => {
                if (!ref) return null;
                if (typeof ref === 'string') {
                  // Split by newlines and render each line separately
                  const lines = ref.split('\n').filter(line => line.trim() !== '');
                  return (
                    <div key={i} style={{ flex: '1 1 45%', minWidth: '180px', fontSize: fontSize, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {lines.map((line, idx) => (
                        <div key={idx} style={{ marginBottom: '2px' }}>{line}</div>
                      ))}
                    </div>
                  );
                }
                const name = ref.refName || ref.name || ref.referenceName || '';
                const company = ref.company || ref.companyName || '';
                const titlePos = ref.position || ref.jobTitle || '';
                return (
                  <div key={i} style={{ flex: '1 1 45%', minWidth: '180px' }}>
                    <h3 style={{ fontSize: fontSize, margin: '0', fontWeight: 'bold' }}>{name}</h3>
                    {(company || titlePos) && <p style={{ margin: '3px 0', color: '#555', fontSize: fontSize }}>{company}{titlePos ? ` / ${titlePos}` : ''}</p>}
                    {ref.phone && <p style={{ margin: '3px 0', fontSize: fontSize }}>Phone: {ref.phone}</p>}
                    {ref.email && <p style={{ margin: '3px 0', fontSize: fontSize }}>Email: {ref.email}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null;
      }

      case 'additionalInfo': {
        const activeInfo = ensureArray(savedAdditionalInfo).filter(Boolean);
        return activeInfo.length > 0 ? (
          <div key="additionalInfo" className="resume42-section" data-section-id="additionalInfo">
            <h2 style={rightHeadingStyle}>ADDITIONAL INFORMATION</h2>
            <ul style={ulStyle}>
              {activeInfo.map((info, i) => {
                const text = typeof info === 'string' ? info : (info?.info || info?.text || info?.description || '');
                return <li key={i} style={liStyle}>{text}</li>;
              })}
            </ul>
          </div>
        ) : null;
      }

      default:
        if (sectionId.startsWith("custom_")) {
          const name = sectionId.replace("custom_", "");
          const items = ensureArray(customSections[name]).filter(Boolean);
          return items.length > 0 ? (
            <div key={sectionId} className="resume42-section" data-section-id={sectionId}>
              <h2 style={rightHeadingStyle}>{name.replace(/_/g, ' ').toUpperCase()}</h2>
              <ul style={ulStyle}>
                {items.map((item, i) => <li key={i} style={liStyle}>{item}</li>)}
              </ul>
            </div>
          ) : null;
        }
        return null;
    }
  };

  // ─── Arrange dialog panel ──────────────────────────────────────────────────
  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3, flex: 1 }}>
      <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
        <DragIndicator sx={{ mr: 1 }} />
        {side === "left" ? "Left Sidebar" : "Main Content"} Sections ({sections.length})
      </Typography>
      <Box sx={{ maxHeight: 400, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 1, p: 1 }}>
        {sections.length > 0 ? (
          sections.map((sectionId, index) => (
            <Paper
              key={sectionId}
              draggable
              onDragStart={mkDragStart(side, sections)}
              onDragOver={(e) => handleDragOver(e, index, side)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index, side)}
              onDragEnd={handleDragEnd}
              sx={{
                p: 2,
                mb: 1,
                display: "flex",
                alignItems: "center",
                backgroundColor: dragOverIndex === index && dragOverSide === side ? "#f0f0f0" : "white",
                boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
                transition: "all 0.2s ease",
              }}
            >
              <DragIndicator color="action" />
              <Typography sx={{ ml: 1, flexGrow: 1, fontSize: '13px' }}>{getSectionTitle(sectionId)}</Typography>
              <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
            </Paper>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: "center" }}>
            No sections. Drag sections from the other side.
          </Typography>
        )}
      </Box>
    </Box>
  );

  // ── Pagination logic (EXACTLY like Resume39) ──────────────────────────
  const availableHeight = pagePx.height - (2 * topBottomMargin);

  const paginateContent = useCallback(() => {
    if (!mainContentRef.current) return;
    const sections = Array.from(mainContentRef.current.querySelectorAll(".resume42-section"));
    if (sections.length === 0) { setPages([{ rightIds: [] }]); return; }

    const newPages = [];
    let currentIds = [];
    let currentHeight = 0;

    sections.forEach(section => {
      const id = section.getAttribute("data-section-id");
      const h = section.offsetHeight;
      const targetAvailableHeight = newPages.length === 0 ? (availableHeight - 160) : availableHeight;

      if (currentHeight + h > targetAvailableHeight && currentIds.length > 0) {
        newPages.push({ rightIds: currentIds });
        currentIds = [id];
        currentHeight = h;
      } else {
        currentIds.push(id);
        currentHeight += h;
      }
    });
    if (currentIds.length > 0) newPages.push({ rightIds: currentIds });
    setPages(newPages);
  }, [
    availableHeight, rightSectionOrder,
    savedSummary, workHistory, educationEntries,
    savedSkills, savedLanguages, savedAccomplishments,
    savedCertifications, softwareSkills, savedVolunteering,
    savedInterests, savedWebsites, savedReferences,
    savedAdditionalInfo, customSections,
    fontSize, headingSize, font, fontStyle, lineSpacing,
    paragraphSpacing, sectionSpacing,
  ]);

  useEffect(() => {
    const id = setTimeout(() => paginateContent(), 400);
    return () => clearTimeout(id);
  }, [paginateContent]);

  // ── Layout Style properties ──────────────────────────────────────────────
  const SIDEBAR_W = Math.round(pagePx.width * 0.38);
  const MAIN_W = pagePx.width - SIDEBAR_W;

  const pageStyle = {
    width: `${pagePx.width}px`,
    minHeight: `${pagePx.height}px`,
    maxWidth: `${pagePx.width}px`,
    overflow: "hidden",
    display: "flex",
    flexDirection: "row",
    backgroundColor: "#ffffff",
    boxShadow: exporting ? "none" : "0 4px 20px rgba(0,0,0,0.15)",
    margin: "0 auto 24px",
    boxSizing: "border-box",
    fontFamily: font,
    fontSize: fontSize,
  };

  const sidebarStyle = {
    width: `${SIDEBAR_W}px`,
    minWidth: `${SIDEBAR_W}px`,
    maxWidth: `${SIDEBAR_W}px`,
    minHeight: `${pagePx.height}px`,
    overflow: "hidden",
    backgroundColor: "#f5e9e1",
    color: "#2d2d2d",
    paddingTop: `${topBottomMargin}px`,
    paddingBottom: `${topBottomMargin}px`,
    paddingLeft: `${Math.max(10, Math.round(sideMargins * 0.6))}px`,
    paddingRight: `${Math.max(10, Math.round(sideMargins * 0.6))}px`,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: `${Math.max(8, Math.round(sectionSpacing * 0.6))}px`,
    flexShrink: 0,
  };

  const mainAreaStyle = {
    width: `${MAIN_W}px`,
    minWidth: 0,
    minHeight: `${pagePx.height}px`,
    paddingTop: `${topBottomMargin}px`,
    paddingBottom: `${topBottomMargin}px`,
    paddingLeft: `${sideMargins}px`,
    paddingRight: `${sideMargins}px`,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: "#ffffff",
  };

  const totalPages = Math.max(1, pages.length);

  return (
    <Box sx={{ width: "100%", position: "relative", m: exporting ? 0 : { xs: 1, md: 5 }, fontFamily: font }}>
      {/* Controls */}
      {!exporting && (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 2, justifyContent: "center", "@media print": { display: "none" } }}>
          <IconButton
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            size="small"
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            Page {currentPage + 1} / {totalPages}
          </Typography>
          <IconButton
            onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            size="small"
          >
            <ChevronRight />
          </IconButton>

          <Button onClick={handleArrangeSectionsClick} variant="contained" startIcon={<Reorder />} sx={{ backgroundColor: color }} size="small">
            Arrange Sections
          </Button>
          <Button onClick={resetAll} variant="outlined" color="secondary" size="small">
            Reset Sections
          </Button>
        </Box>
      )}

      {/* Hidden measurement container — like Resume39 */}
      <Box
        ref={mainContentRef}
        sx={{
          position: "absolute",
          top: -9999,
          visibility: "hidden",
          pointerEvents: "none",
          width: `${MAIN_W}px`,
          paddingLeft: `${sideMargins}px`,
          paddingRight: `${sideMargins}px`,
          fontFamily: font,
          fontSize: fontSize,
          boxSizing: "border-box",
        }}
      >
        {rightSectionOrder.map((id) => {
          const content = renderRightSection(id);
          return content ? <React.Fragment key={id}>{content}</React.Fragment> : null;
        })}
      </Box>

      {/* Resume Pages */}
      {(pages.length > 0 ? pages : [{ rightIds: null }]).map((pageContent, pageIdx) => (
        <div
          key={pageIdx}
          className="resume-page"
          style={{
            ...pageStyle,
            display: exporting ? "flex" : (pageIdx === currentPage ? "flex" : "none"),
          }}
        >
          {/* LEFT COLUMN - SIDEBAR */}
          <div style={sidebarStyle}>
            {/* Photo on Page 1 only */}
            {pageIdx === 0 && photo && (
              <div>
                <img
                  src={getImageSource(photo)}
                  alt="Profile"
                  style={{
                    width: "100%",
                    maxWidth: "140px",
                    height: "auto",
                    borderRadius: "8px",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}

            {/* Left sections - only on page 1 */}
            {pageIdx === 0 && leftSectionOrder.map((sectionId) => {
              const content = renderLeftSection(sectionId);
              return content ? <React.Fragment key={sectionId}>{content}</React.Fragment> : null;
            })}
          </div>

          {/* RIGHT COLUMN - MAIN CONTENT */}
          <div style={mainAreaStyle}>
            {/* Name & Title — only on page 1 */}
            {pageIdx === 0 && (
              <div>
                <h1
                  style={{
                    fontSize: `calc(${headingSize} * 1.5)`,
                    fontWeight: "bold",
                    margin: "0",
                    lineHeight: "1.1",
                    color: "#3e2723",
                    textTransform: "uppercase"
                  }}
                >
                  {fullName.split(' ')[0]} <span style={{ color }}>{fullName.split(' ').slice(1).join(' ')}</span>
                </h1>
                {position && (
                  <p
                    style={{
                      fontSize: `calc(${fontSize} * 1.1)`,
                      margin: "6px 0 0 0",
                      color: "#6d4c41",
                      fontWeight: "500",
                      textTransform: "uppercase"
                    }}
                  >
                    {position}
                  </p>
                )}
                <div style={{ borderBottom: `${lineWeight}px solid #d7ccc8`, marginTop: `${sectionSpacing / 6}px` }} />
              </div>
            )}

            {/* Right sections for current page */}
            {rightSectionOrder.map((sectionId) => {
              const content = renderRightSection(sectionId, pageContent.rightIds);
              return content ? <React.Fragment key={sectionId}>{content}</React.Fragment> : null;
            })}
          </div>
        </div>
      ))}

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Reorder /> Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag sections to reorder, or move them between sidebar and main content.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
            {renderDragPanel("left", leftSectionOrder)}
            {renderDragPanel("right", rightSectionOrder)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAll} color="secondary">Reset All</Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume42;